import { type ReadonlyMat2d, type ReadonlyVec2, vec2 } from 'gl-matrix';

import { type Direction, dirFromVec2 } from '../direction/direction';
import { type Range, rangeIntersection } from '../spatial/range';

import { boxGlobalToNormalizedLocal, boxToMat2dRotatedNormalized } from './transform';

/**
 *  Dimensions describing the perimeter of one or more entities.
 *  Top is a smaller value than bottom.
 *  Left is a smaller value than right.
 */
export type Box = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

/**
 * Use this in function signatures to tell developers and our type system that
 * the Box is only being used for reading and will not be mutated.
 */
export type ReadonlyBox = Readonly<Box>;

/**
 * Creates a box, defaulting to 0 for all values
 * @returns Box
 */
export function boxCreate(left = 0, right = 0, top = 0, bottom = 0): Box {
  return {
    left,
    right,
    top,
    bottom,
  };
}

/**
 * Creates a box from a center point and a width and height
 * @param centerX The center of the box
 * @param centerY The center of the box
 * @param width The width of the box
 * @param height The height of the box
 * @returns The box
 */
export function boxCreateCentered(centerX = 0, centerY = 0, width = 0, height = 0): Box {
  return {
    left: centerX - width / 2,
    right: centerX + width / 2,
    top: centerY - height / 2,
    bottom: centerY + height / 2,
  };
}

/**
 * Creates a box from a point. Note that the box's left and right values will be
 * equal. Same goes for the top and bottom.
 */
export const pointToBox = (point: ReadonlyVec2): Box =>
  boxCreate(point[0], point[0], point[1], point[1]);

/**
 * Clones a box
 * @params box: Box
 * @returns Box
 */
export function boxClone(box: ReadonlyBox): Box {
  return {
    left: box.left,
    right: box.right,
    top: box.top,
    bottom: box.bottom,
  };
}

/**
 * Copies values from one box into another
 * @params out: Box - the box receiving the values
 * @params from: Box - the box providing the values
 * @returns void
 */
export function boxCopy(out: Box, from: ReadonlyBox): void {
  out.left = from.left;
  out.right = from.right;
  out.top = from.top;
  out.bottom = from.bottom;
}

/**
 * Sets all values in a box
 * @param out: Box - the box receiving the values
 * @param left: number - the left coordinate of the box
 * @param right: number - the right coordinate of the box
 * @param top: number - the top coordinate of the box
 * @param bottom: number - the bottom coordinate of the box
 * @returns void
 */
export function boxSet(
  out: Box,
  left: number,
  right: number,
  top: number,
  bottom: number
): void {
  out.left = left;
  out.right = right;
  out.top = top;
  out.bottom = bottom;
}

/**
 * Gets the top left point of a box
 * @params box: Box
 * @returns vec2, the position of the top left corner of the box
 */
export function boxTopLeft(box: ReadonlyBox): vec2 {
  return vec2.fromValues(box.left, box.top);
}

/**
 * Gets the bottom left point of a box
 * @params box: Box
 * @returns vec2, the position of the bottom left corner of the box
 */
export function boxBottomLeft(box: ReadonlyBox): vec2 {
  return vec2.fromValues(box.left, box.bottom);
}

/**
 * Gets the top right point of a box
 * @params box: Box
 * @returns vec2, the position of the top right corner of the box
 */
export function boxTopRight(box: ReadonlyBox): vec2 {
  return vec2.fromValues(box.right, box.top);
}

/**
 * Gets the bottom right point of a box
 * @params box: Box
 * @returns vec2, the position of the bottom right corner of the box
 */
export function boxBottomRight(box: ReadonlyBox): vec2 {
  return vec2.fromValues(box.right, box.bottom);
}

/**
 * Gets the width of a box
 * @params box: Box
 * @returns number, the width
 */
export function boxWidth(box: ReadonlyBox): number {
  return box.right - box.left;
}

/**
 * Gets the height of a box
 * @params box: Box
 * @returns number, the height
 */
export function boxHeight(box: ReadonlyBox): number {
  return box.bottom - box.top;
}

/**
 * Gets the width and height of a box as a vec2
 * @params box: Box
 * @returns vec2, the width and height
 */
export function boxSize(box: ReadonlyBox): vec2 {
  return vec2.fromValues(box.right - box.left, box.bottom - box.top);
}

/**
 * Gets the horizontal (x) center of a box
 * @params box: Box
 * @returns number, the x position of the center
 */
export function boxCenterX(box: ReadonlyBox): number {
  return (box.left + box.right) / 2;
}

/**
 * Gets the vertical (y) center of a box
 * @params box: Box
 * @returns number, the x position of the center
 */
export function boxCenterY(box: ReadonlyBox): number {
  return (box.top + box.bottom) / 2;
}

/**
 * Gets the center of a box
 * @params box: Box
 * @returns vec2, the x and y position of the center
 */
export function boxCenter(box: ReadonlyBox, result = vec2.create()): vec2 {
  result[0] = boxCenterX(box);
  result[1] = boxCenterY(box);
  return result;
}

/**
 * Version of {@link boxUnion} that's designed to only operates on a single new
 * box and does not check NaN.
 *
 * Use if the assumptions work for you and performance is critical.
 */
export const boxUnionSingleUnsafe = (newBox: ReadonlyBox, out: Box) => {
  out.left = Math.min(out.left, newBox.left);
  out.right = Math.max(out.right, newBox.right);
  out.top = Math.min(out.top, newBox.top);
  out.bottom = Math.max(out.bottom, newBox.bottom);
};

/**
 * Return the union of an array of boxes
 * a.k.a. the box around a set of boxes
 * @params boxes: Box[] - the boxes to take the union of
 * @returns Box
 */
export function boxUnion(
  boxes: ReadonlyBox[],
  out = {
    left: Infinity,
    right: -Infinity,
    top: Infinity,
    bottom: -Infinity,
  }
): Box {
  for (let i = 0; i < boxes.length; i++) {
    if (!boxHasValues(boxes[i])) {
      continue;
    }

    boxUnionSingleUnsafe(boxes[i], out);
  }

  return out;
}

/**
 * Resets a box to its initial state for recomputing unions
 * @params union box to reset
 * @returns reset box, ready for recomputing unions
 */
export function boxUnionReset(box: Box): Box {
  box.left = Infinity;
  box.right = -Infinity;
  box.top = Infinity;
  box.bottom = -Infinity;

  return box;
}

/**
 * Tests if a box is still in its initial state for recomputing unions
 * i.e. the box has not been altered since calling boxUnionReset() on it
 *
 * @param box box to test
 * @returns true if the box is still in a reset state, false otherwise
 */
export function boxIsUnionReset(box: ReadonlyBox): boolean {
  // we only check .left property here because any union additions to the box
  // would also set all other properties to be finite
  return box.left === Infinity;
}

export function boxHasValues(box: ReadonlyBox): boolean {
  return !isNaN(box.left) && !isNaN(box.right) && !isNaN(box.top) && !isNaN(box.bottom);
}

/**
 * Grows (or shrinks) all sides of a box by an amount. Negative values will shrink the box.
 * @params box: Box - initial box
 * @params amount: number - amount to push each side out
 * @returns Box
 */
export function boxExpand(box: ReadonlyBox, amount: number, out = boxCreate()): Box {
  out.left = box.left - amount;
  out.right = box.right + amount;
  out.top = box.top - amount;
  out.bottom = box.bottom + amount;

  if (out.left > out.right) {
    const center = (out.left + out.right) / 2;
    out.left = center;
    out.right = center;
  }

  if (out.top > out.bottom) {
    const center = (out.bottom + out.top) / 2;
    out.bottom = center;
    out.top = center;
  }

  return out;
}

/**
 * Expands the side of a box in the given direction by the given amount.
 *
 * @params box: Box - the box to expand
 * @params direction: Direction - the direction of the expansion.
 * @params amount: number - the amount to expand the box by.
 * @returns Box - the box passed in, mutated
 */
export function boxExpandInDirection(
  box: Box,
  direction: Direction,
  amount: number
): Box {
  if (direction === '+x') {
    box.right += amount;
  } else if (direction === '-x') {
    box.left -= amount;
  } else if (direction === '+y') {
    box.bottom += amount;
  } else if (direction === '-y') {
    box.top -= amount;
  }
  return box;
}

/**
 * Expands a box to fit the given point.
 * Useful for calculating bounding boxes from a series of points
 *
 * @params box: Box - the box to expand
 * @params point: ReadonlyVec2 - the point to expand to include
 * @returns Box - the box passed in
 */
export function boxExpandToFitPoint(box: Box, point: ReadonlyVec2): Box {
  const x = point[0] || 0;
  const y = point[1] || 0;
  if (x < box.left) {
    box.left = x;
  }
  if (x > box.right) {
    box.right = x;
  }
  if (y < box.top) {
    box.top = y;
  }
  if (y > box.bottom) {
    box.bottom = y;
  }
  return box;
}

const normalisedCenter = vec2.fromValues(0.5, 0.5);
const localPointFlyweight = vec2.create();

/**
 * Expands a box to fit the given box.
 *
 * @params boxToExpand: Box - the box to expand, that will be mutated
 * @params boxToFit: Box - the box to fit
 * @returns Box - boxToExpand
 */
export function boxExpandToFitBox(boxToExpand: Box, boxToFit: ReadonlyBox): Box {
  if (boxToFit.left < boxToExpand.left) {
    boxToExpand.left = boxToFit.left;
  }
  if (boxToFit.top < boxToExpand.top) {
    boxToExpand.top = boxToFit.top;
  }
  if (boxToFit.right > boxToExpand.right) {
    boxToExpand.right = boxToFit.right;
  }
  if (boxToFit.bottom > boxToExpand.bottom) {
    boxToExpand.bottom = boxToFit.bottom;
  }
  return boxToExpand;
}

/**
 * Get direction of a point relative to a box.
 * @param box
 * @param point
 * @returns
 */
export function boxRelativeDirection(box: ReadonlyBox, point: ReadonlyVec2): Direction {
  const localPoint = vec2.sub(
    localPointFlyweight,
    boxGlobalToNormalizedLocal(box, point),
    normalisedCenter
  );

  return dirFromVec2(localPoint);
}

/**
 * @returns the box formed by the intersection of two boxes. Note that top,
 * right, bottom and/or left will be an illogical/invalid value if the boxes do
 * not intersecting (E.g. right < left and/or bottom < top)
 */
export const boxIntersectionBounds = (box1: ReadonlyBox, box2: ReadonlyBox) => ({
  top: Math.max(box1.top, box2.top),
  right: Math.min(box1.right, box2.right),
  bottom: Math.min(box1.bottom, box2.bottom),
  left: Math.max(box1.left, box2.left),
});

export const boxArea = (box: ReadonlyBox): number =>
  (box.right - box.left) * (box.bottom - box.top);

/**
 * Returns true if two boxes are equal, false otherwise.
 */
export function boxEqual(a: ReadonlyBox, b: ReadonlyBox): boolean {
  return (
    a === b ||
    (a.left === b.left && a.right === b.right && a.top === b.top && a.bottom === b.bottom)
  );
}

/**
 * gets the value of the edge of the box
 * that is toward the specified direction
 * e.g. +x direction gets the value of the right edge of the box
 *
 * @param box the box
 * @param dir the direction
 * @returns number value of the edge
 */
export function boxGetEdgeAtDirection(box: ReadonlyBox, dir: Direction): number {
  if (dir === '+x') {
    return box.right;
  }
  if (dir === '-x') {
    return box.left;
  }
  if (dir === '+y') {
    return box.bottom;
  }
  return box.top;
}

/**
 * gets both vec2s that represent a single edge on a bounding box
 *
 * @param box the box
 * @param dir the direction
 * @returns both vec2s that represent the side
 */
export function boxGetWholeEdgeAtDirection(
  box: ReadonlyBox,
  dir: Direction
): [vec2, vec2] {
  if (dir === '+x') {
    return [vec2.fromValues(box.right, box.top), vec2.fromValues(box.right, box.bottom)];
  }
  if (dir === '-x') {
    return [vec2.fromValues(box.left, box.top), vec2.fromValues(box.left, box.bottom)];
  }
  if (dir === '+y') {
    return [
      vec2.fromValues(box.left, box.bottom),
      vec2.fromValues(box.right, box.bottom),
    ];
  }
  return [vec2.fromValues(box.left, box.top), vec2.fromValues(box.right, box.top)];
}

const corners = [
  vec2.fromValues(0, 0),
  vec2.fromValues(1, 0),
  vec2.fromValues(0, 1),
  vec2.fromValues(1, 1),
];

const rotatedPointFlyweight = vec2.create();

/**
 * Returns the axis-aligned bounding box
 * of a rotated box.
 *
 * @param box the rotated box
 * @param rotation optional amount of radians clockwise that box is rotated, defaults to 0
 * @param rotationOrigin optional point around which the box is rotated, defaults to box centre
 * @param result optional box to mutate the result bounding box onto
 * @returns the determined bounding box
 */
export function boxGetBoundsOfRotatedBox(
  box: ReadonlyBox,
  rotation = 0,
  rotationOrigin: ReadonlyVec2 = boxCenter(box),
  result = boxCreate()
): Box {
  const localToGlobal = boxToMat2dRotatedNormalized(box, rotation, rotationOrigin);
  boxUnionReset(result);
  for (const point of corners) {
    vec2.transformMat2d(rotatedPointFlyweight, point, localToGlobal);
    boxExpandToFitPoint(result, rotatedPointFlyweight);
  }
  return result;
}

/**
 * Calculates the vector from the center of the box to the edge in the given direction.
 * @param width width of the box, half of the width
 * @param height height of the box, half of the height
 * @param directionVec vec2, the direction vector to the edge
 * @param out vec2, the output vector
 * @returns vec2, the vector from the center of the box to the edge
 */
export const boxGetEdgeVectorFromCenter = (
  width: number,
  height: number,
  directionVec: ReadonlyVec2,
  out = vec2.create()
) => {
  const dx = directionVec[0];
  const dy = directionVec[1];

  const halfW = width / 2;
  const halfH = height / 2;

  // How far we can go along direction before hitting an edge
  const tx = dx !== 0 ? halfW / Math.abs(dx) : Infinity;
  const ty = dy !== 0 ? halfH / Math.abs(dy) : Infinity;
  let t = Math.min(tx, ty);

  if (dx === 0 && dy === 0) {
    t = 0;
  }
  // The resulting vector from center to edge
  return vec2.scale(out, directionVec, t);
};

// Flyweight used by `boxLines` to avoid creating new vector instances
const boxLinesFlyweight: [vec2, vec2] = [vec2.create(), vec2.create()];

/**
 * Generates the four lines of a box as an iterable of vec2 pairs.
 *
 * @remarks The yielded lines are reused across iterations to avoid unnecessary
 * allocations. You must access them immediately or clone them if you need to
 * keep them for later use (e.g. if converting to an array).
 * An optional transform can be provided to transform the lines into a different
 * coordinate space.
 */
export function* boxLines(
  box: ReadonlyBox,
  transform?: ReadonlyMat2d
): Generator<[vec2, vec2]> {
  boxLinesFlyweight[0][0] = box.left;
  boxLinesFlyweight[0][1] = box.top;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[0], boxLinesFlyweight[0], transform);
  }

  boxLinesFlyweight[1][0] = box.right;
  boxLinesFlyweight[1][1] = box.top;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[1], boxLinesFlyweight[1], transform);
  }

  yield boxLinesFlyweight;

  boxLinesFlyweight[0][0] = box.right;
  boxLinesFlyweight[0][1] = box.top;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[0], boxLinesFlyweight[0], transform);
  }

  boxLinesFlyweight[1][0] = box.right;
  boxLinesFlyweight[1][1] = box.bottom;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[1], boxLinesFlyweight[1], transform);
  }

  yield boxLinesFlyweight;

  boxLinesFlyweight[0][0] = box.right;
  boxLinesFlyweight[0][1] = box.bottom;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[0], boxLinesFlyweight[0], transform);
  }

  boxLinesFlyweight[1][0] = box.left;
  boxLinesFlyweight[1][1] = box.bottom;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[1], boxLinesFlyweight[1], transform);
  }

  yield boxLinesFlyweight;

  boxLinesFlyweight[0][0] = box.left;
  boxLinesFlyweight[0][1] = box.bottom;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[0], boxLinesFlyweight[0], transform);
  }

  boxLinesFlyweight[1][0] = box.left;
  boxLinesFlyweight[1][1] = box.top;
  if (transform) {
    vec2.transformMat2d(boxLinesFlyweight[1], boxLinesFlyweight[1], transform);
  }

  yield boxLinesFlyweight;
}

/**
 * Calculates the horizontal whitespace between two boxes.
 *
 * @returns
 * Positive: The width of the empty space between boxes
 * Zero: The boxes are perfectly flush/touching
 * Negative: The depth of the overlap (the minimum distance to separate them)
 */
export function horizontalBoxGap(a: ReadonlyBox, b: ReadonlyBox): number {
  return Math.max(b.left - a.right, a.left - b.right);
}

/**
 * Calculates the vertical whitespace between two boxes.
 *
 * @returns
 * Positive: The height of the empty space between boxes
 * Zero: The boxes are perfectly flush/touching
 * Negative: The depth of the overlap (the minimum distance to separate them)
 */
export function verticalBoxGap(a: ReadonlyBox, b: ReadonlyBox): number {
  return Math.max(b.top - a.bottom, a.top - b.bottom);
}

/**
 * Returns the horizontal (X-axis) overlap range between two boxes, or null if they don't overlap.
 */
export function boxOverlapHorizontal(a: ReadonlyBox, b: ReadonlyBox): Range | null {
  return rangeIntersection(
    { start: a.left, end: a.right },
    { start: b.left, end: b.right }
  );
}

/**
 * Returns the vertical (Y-axis) overlap range between two boxes, or null if they don't overlap.
 */
export function boxOverlapVertical(a: ReadonlyBox, b: ReadonlyBox): Range | null {
  return rangeIntersection(
    { start: a.top, end: a.bottom },
    { start: b.top, end: b.bottom }
  );
}

/**
 * Rounds the values of a box to the nearest integer.
 * @param box The box to round
 * @returns The rounded box
 */
export function boxRound(box: ReadonlyBox): Box {
  return {
    left: Math.round(box.left),
    right: Math.round(box.right),
    top: Math.round(box.top),
    bottom: Math.round(box.bottom),
  };
}

/**
 * Given a point, return the closest point on the edge of the box.
 */
export function getClosestPointOnBoxEdge(box: ReadonlyBox, point: ReadonlyVec2): vec2 {
  const minX = Math.min(box.left, box.right);
  const maxX = Math.max(box.left, box.right);
  const minY = Math.min(box.top, box.bottom);
  const maxY = Math.max(box.top, box.bottom);

  const x = point[0];
  const y = point[1];

  // Handles points OUTSIDE the box (snaps to nearest edge/corner)
  let closestX = Math.max(minX, Math.min(x, maxX));
  let closestY = Math.max(minY, Math.min(y, maxY));

  // Handles points inside the box, pushes it to the nearest edge.
  const isInside =
    closestX > minX && closestX < maxX && closestY > minY && closestY < maxY;

  if (isInside) {
    const distToLeft = closestX - minX;
    const distToRight = maxX - closestX;
    const distToTop = closestY - minY;
    const distToBottom = maxY - closestY;

    // Find the smallest distance to an edge
    const minDst = Math.min(distToLeft, distToRight, distToTop, distToBottom);

    // Snap to that edge
    if (minDst === distToLeft) {
      closestX = minX;
    } else if (minDst === distToRight) {
      closestX = maxX;
    } else if (minDst === distToTop) {
      closestY = minY;
    } else {
      closestY = maxY;
    }
  }

  return vec2.fromValues(closestX, closestY);
}

export function isPointOnBoxCorner(box: ReadonlyBox, point: ReadonlyVec2): boolean {
  return (
    (point[0] === box.left && point[1] === box.top) ||
    (point[0] === box.right && point[1] === box.top) ||
    (point[0] === box.left && point[1] === box.bottom) ||
    (point[0] === box.right && point[1] === box.bottom)
  );
}
